import { useEffect, useRef, useState } from 'react';
import Peaks from 'peaks.js';
import { Box, Button, CircularProgress, IconButton, Slider, TextField, Typography } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';

const ZOOM_LEVELS = [16, 32, 64, 128, 256, 512];

function detectImageMime(b64) {
  if (b64.startsWith('iVBOR')) return 'image/png';
  if (b64.startsWith('/9j/')) return 'image/jpeg';
  if (b64.startsWith('R0lGOD')) return 'image/gif';
  if (b64.startsWith('UklGR')) return 'image/webp';
  return 'image/png';
}

const BACKEND_URL = import.meta.env.VITE_APP_API_BACKEND_URL;

const PROXY_HOSTS = ['storage.googleapis.com', 'storage.cloud.google.com'];

async function resolveMediaSrc(url) {
  let res;
  let usedProxy = false;
  const needsProxy = PROXY_HOSTS.some((h) => url.includes(h));
  if (!needsProxy) {
    try {
      res = await fetch(url);
    } catch {
      // fall through to proxy
    }
  }
  if (!res) {
    res = await fetch(`${BACKEND_URL}/proxy-media?url=${encodeURIComponent(url)}`);
    usedProxy = true;
  }
  const contentType = res.headers.get('content-type') ?? '';

  if (contentType.startsWith('audio/') || contentType.startsWith('application/octet-stream')) {
    if (usedProxy) {
      const blob = await res.blob();
      return { src: URL.createObjectURL(blob), isBlob: true, imageSrc: null };
    }
    return { src: url, isBlob: false, imageSrc: null };
  }

  if (contentType.includes('xml')) {
    const text = await res.text();
    const doc = new DOMParser().parseFromString(text, 'text/xml');
    const audioB64 = doc.querySelector('Audio')?.textContent?.trim();
    if (!audioB64) throw new Error('No <Audio> element found in XML response');
    const audioBytes = Uint8Array.from(atob(audioB64), (c) => c.charCodeAt(0));
    const audioBlob = new Blob([audioBytes], { type: 'audio/wav' });
    const imageB64 = doc.querySelector('Image')?.textContent?.trim() ?? null;
    const imageSrc = imageB64 ? `data:${detectImageMime(imageB64)};base64,${imageB64}` : null;
    return { src: URL.createObjectURL(audioBlob), isBlob: true, imageSrc };
  }

  if (contentType.includes('text/html')) {
    const arrayBuffer = await res.arrayBuffer();
    const header = new Uint8Array(arrayBuffer.slice(0, 4));
    const magic = String.fromCharCode(...header);
    const audioMagic =
      magic === 'RIFF' ? 'audio/wav' :
      magic === 'OggS' ? 'audio/ogg' :
      magic === 'fLaC' ? 'audio/flac' :
      magic.startsWith('ID3') ? 'audio/mpeg' :
      (header[0] === 0xFF && (header[1] & 0xE0) === 0xE0) ? 'audio/mpeg' :
      null;

    if (audioMagic) {
      const blob = new Blob([arrayBuffer], { type: audioMagic });
      return { src: URL.createObjectURL(blob), isBlob: true, imageSrc: null };
    }

    const html = new TextDecoder().decode(arrayBuffer);
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const audioSrc =
      doc.querySelector('audio[src]')?.getAttribute('src') ??
      doc.querySelector('audio source[src]')?.getAttribute('src') ??
      doc.querySelector('source[src]')?.getAttribute('src');
    if (!audioSrc) throw new Error('No audio source found in HTML response');

    const imgEl = doc.querySelector('img[src]');
    const imgSrc = imgEl?.getAttribute('src') ?? null;
    const imageSrc = imgSrc
      ? imgSrc.startsWith('data:') ? imgSrc : new URL(imgSrc, url).href
      : null;

    if (audioSrc.startsWith('data:')) {
      const [meta, b64] = audioSrc.split(',');
      const mime = meta.match(/data:([^;]+)/)?.[1] ?? 'audio/wav';
      const audioBytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
      const audioBlob = new Blob([audioBytes], { type: mime });
      return { src: URL.createObjectURL(audioBlob), isBlob: true, imageSrc };
    }

    return { src: new URL(audioSrc, url).href, isBlob: false, imageSrc };
  }

  if (usedProxy) {
    const blob = await res.blob();
    return { src: URL.createObjectURL(blob), isBlob: true, imageSrc: null };
  }
  return { src: url, isBlob: false, imageSrc: null };
}

export { resolveMediaSrc };

export default function WaveformPlayer({ audioFileURL }) {
  const audioRef = useRef(null);
  const zoomviewRef = useRef(null);
  const overviewRef = useRef(null);
  const peaksRef = useRef(null);
  const blobUrlRef = useRef(null);

  const [loaded, setLoaded] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState('');
  const [zoomSeconds, setZoomSeconds] = useState(1);
  const [imageSrc, setImageSrc] = useState(null);
  const [fallbackSrc, setFallbackSrc] = useState(null);
  const [imageExpanded, setImageExpanded] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);

  function destroyPeaks() {
    if (peaksRef.current) {
      peaksRef.current.destroy();
      peaksRef.current = null;
    }
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    setLoaded(false);
    setPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setError('');
    setZoomSeconds(1);
    setImageSrc(null);
    setFallbackSrc(null);
    setPlaybackRate(1);
  }

  function initPeaks(src) {
    const audio = audioRef.current;
    const zoomEl = zoomviewRef.current;
    const overviewEl = overviewRef.current;
    if (!audio || !zoomEl || !overviewEl || !src) return;

    audio.src = src;

    Peaks.init(
      {
        mediaElement: audio,
        zoomview: {
          container: zoomEl,
          waveformColor: '#6366f1',
          playedWaveformColor: '#312e81',
          playheadColor: '#f59e0b',
          axisLabelColor: '#6b7280',
          axisGridlineColor: '#e5e7eb',
          wheelMode: 'scroll',
          autoScroll: true,
          autoScrollOffset: 20,
        },
        overview: {
          container: overviewEl,
          waveformColor: '#6366f1',
          playedWaveformColor: '#312e81',
          playheadColor: '#f59e0b',
        },
        zoomLevels: ZOOM_LEVELS,
        webAudio: {
          audioContext: new AudioContext(),
          scale: 512,
          multiChannel: false,
        },
        logger: console.error,
      },
      (err, peaks) => {
        if (err || !peaks) {
          setFallbackSrc(src);
          return;
        }
        peaksRef.current = peaks;
        const dur = peaks.player.getDuration();
        setDuration(dur);
        setZoomSeconds(1);
        peaks.views.getView('zoomview')?.setZoom({ scale: 'auto' });
        peaks.on('player.timeupdate', (time) => setCurrentTime(time));
        peaks.on('player.playing', () => setPlaying(true));
        peaks.on('player.pause', () => setPlaying(false));
        peaks.on('player.ended', () => setPlaying(false));
        setLoaded(true);
      },
    );
  }

  useEffect(() => {
    if (!audioFileURL) return;
    let cancelled = false;
    destroyPeaks();

    resolveMediaSrc(audioFileURL)
      .then(({ src, isBlob, imageSrc: img }) => {
        if (cancelled) {
          if (isBlob) URL.revokeObjectURL(src);
          return;
        }
        if (isBlob) blobUrlRef.current = src;
        if (img) setImageSrc(img);
        initPeaks(src);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message ?? 'Failed to load audio');
      });

    return () => {
      cancelled = true;
      destroyPeaks();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioFileURL]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = playbackRate;
  }, [playbackRate]);

  function togglePlay() {
    const p = peaksRef.current;
    if (!p) return;
    if (playing) p.player.pause();
    else p.player.play();
  }

  function handleZoom(_, val) {
    setZoomSeconds(val);
    const seconds = duration + 1 - val;
    peaksRef.current?.views.getView('zoomview')?.setZoom({ seconds });
  }

  const fmt = (s) => {
    const m = Math.floor(s / 60);
    const sec = (s % 60).toFixed(3).padStart(6, '0');
    return `${m}:${sec}`;
  };

  return (
    <Box sx={{ width: '100%', p: 1 }}>
      <audio ref={audioRef} style={{ display: 'none' }} />

      {imageSrc && (
        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button size="small" variant="outlined" onClick={() => setImageExpanded((v) => !v)}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
              {imageExpanded ? 'Hide Image' : 'View Image'}
            </Button>
          </Box>
          {imageExpanded && (
            <Box sx={{ mt: 1, textAlign: 'center' }}>
              <img src={imageSrc} alt="file preview"
                style={{ maxWidth: '100%', maxHeight: 300, objectFit: 'contain', borderRadius: 4 }} />
            </Box>
          )}
        </Box>
      )}

      <Box
        ref={zoomviewRef}
        sx={{ width: '100%', height: fallbackSrc ? 0 : 150, background: '#fff', borderRadius: 1, mb: fallbackSrc ? 0 : 1, overflow: 'hidden' }}
      />

      <Box
        ref={overviewRef}
        sx={{ width: '100%', height: fallbackSrc ? 0 : 50, background: '#fff', borderRadius: 1, mb: fallbackSrc ? 0 : 1, overflow: 'hidden' }}
      />

      {!loaded && !error && !fallbackSrc && (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={24} />
        </Box>
      )}

      {fallbackSrc && (
        <Box sx={{ width: '100%', px: 1, pb: 1 }}>
          <audio controls src={fallbackSrc} style={{ width: '100%' }} />
        </Box>
      )}

      {error && (
        <Typography color="error" variant="caption" display="block" textAlign="center" py={1}>
          {error}
        </Typography>
      )}

      {loaded && !fallbackSrc && (
        <Box display="flex" alignItems="center" gap={1} px={1}>
          <IconButton onClick={togglePlay} size="small" color="primary">
            {playing ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
          <Typography variant="caption" sx={{ minWidth: 110, fontVariantNumeric: 'tabular-nums' }}>
            {fmt(currentTime)} / {fmt(duration)}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            Zoom
          </Typography>
          <Slider
            size="small"
            min={1}
            max={Math.max(duration, 1)}
            step={0.5}
            value={zoomSeconds}
            onChange={handleZoom}
            sx={{ flex: 1 }}
          />
          <Autocomplete
            freeSolo
            size="small"
            options={['0.5', '0.75', '1', '1.25', '1.5', '2']}
            value={String(playbackRate)}
            onChange={(_, val) => {
              const n = parseFloat(val);
              if (!isNaN(n) && n >= 0.1 && n <= 4) setPlaybackRate(n);
            }}
            onInputChange={(_, val) => {
              const n = parseFloat(val);
              if (!isNaN(n) && n >= 0.1 && n <= 4) setPlaybackRate(n);
            }}
            disableClearable
            sx={{ width: 80 }}
            renderInput={({ inputProps, ...params }) => (
              <TextField {...params} size="small" label="Speed"
                slotProps={{ htmlInput: { ...inputProps, style: { fontSize: '0.75rem', padding: '2px 4px' } } }} />
            )}
            getOptionLabel={(o) => `${o}x`}
          />
        </Box>
      )}
    </Box>
  );
}
